import React, { useEffect, useId, useRef, useState } from 'react';
import proService from '../../services/proService';
import { ClubPanel, PanelHead, PillButton, TagPill, INSET, MUTED, RULE } from '../club/clubUi';
import { SearchIcon } from '../hand/icons';

/** One linked club per page; choices come only from the server's management scope. */
export default function ProClubSettings({ pageId, linkedClub, onSaved, phone = false }) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [active, setActive] = useState(-1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const busy = useRef(false);
  useEffect(() => {
    let current = true;
    setLoading(true); setError(''); setActive(-1);
    const timer = setTimeout(async () => {
      const result = await proService.suggestClubs(pageId, query.trim());
      if (!current) return;
      setRows(result.success ? result.data || [] : []);
      setError(result.success ? '' : result.error || 'Could not load your clubs.');
      setLoading(false);
    }, query.trim() ? 150 : 0);
    return () => { current = false; clearTimeout(timer); };
  }, [pageId, query]);
  const save = async (club) => {
    if (busy.current || club?.id === linkedClub?.id) return;
    busy.current = true; setSaving(true); setError(''); setSaved(false);
    try {
      const result = await proService.setClub(pageId, club?.id || null);
      if (result.success) { onSaved(result.data?.linkedClub || null); setSaved(true); }
      else setError(result.error || 'Could not save the club.');
    } finally { busy.current = false; setSaving(false); }
  };
  return <ClubPanel phone={phone} data-testid="pro-club-settings">
    <PanelHead title="CLUBS" right={saving ? 'SAVING…' : saved ? 'SAVED' : 'ONE CLUB PER PAGE'} />
    <p style={{ color: MUTED, fontSize: 12 }}>Link a club you own or manage. The CLUB tab on your Pro page will open it.</p>
    {linkedClub && <div className="flex items-center justify-between" style={{ gap: 12, padding: '14px 0', borderBottom: RULE }}>
      <div><TagPill>LINKED</TagPill><p style={{ marginTop: 8 }}>{linkedClub.name}</p></div>
      <PillButton disabled={saving} onClick={() => save(null)}>REMOVE</PillButton>
    </div>}
    <div className="flex items-center" style={{ gap: 10, padding: '0 18px', height: 48, borderRadius: 999, background: INSET }}>
      <SearchIcon size={15} />
      <input role="combobox" aria-label="Search clubs you manage" aria-autocomplete="list" aria-expanded={open} aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        className="min-w-0 flex-1 bg-transparent outline-none font-mono" style={{ fontSize: 12 }}
        placeholder="SEARCH YOUR CLUBS" value={query} disabled={saving}
        onFocus={() => setOpen(true)} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault(); setOpen(true);
            setActive((a) => rows.length ? (a + (e.key === 'ArrowDown' ? 1 : rows.length - 1) + rows.length) % rows.length : -1);
          }
          if (e.key === 'Enter' && open && active >= 0 && !loading) { e.preventDefault(); save(rows[active]); }
        }} />
    </div>
    {error && <p role="alert" style={{ color: '#B91C1C', fontSize: 12 }}>{error}</p>}
    {loading && <p role="status" style={{ color: MUTED }}>LOADING CLUBS…</p>}
    {open && !loading && <div id={listId} role="listbox" aria-label="Clubs you manage">
      {rows.map((club, i) => <button key={club.id} id={`${listId}-${i}`} role="option" aria-selected={linkedClub?.id === club.id}
        disabled={saving} onClick={() => save(club)} className="flex w-full items-center text-left font-mono"
        style={{ gap: 12, padding: 14, borderRadius: 16, background: active === i ? INSET : 'transparent', border: 0, borderBottom: RULE }}>
        {club.avatarUrl && <img src={club.avatarUrl} alt="" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }} />}
        <span className="min-w-0 flex-1">{club.name}</span>
        <TagPill>{linkedClub?.id === club.id ? 'LINKED' : club.role === 'owner' ? 'OWNER' : 'MANAGER'}</TagPill>
      </button>)}
      {!rows.length && !error && <p style={{ padding: 14, color: MUTED, fontSize: 12 }}>{query ? 'No managed clubs match your search.' : 'You don’t own or manage any clubs yet.'}</p>}
    </div>}
  </ClubPanel>;
}
